import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import {
  GripVertical,
  Edit,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  Mouse,
  Type,
  Navigation,
  Camera,
  Scroll,
  Clipboard,
  MoreHorizontal,
  Bot,
  CheckCircle,
  X,
  Mail
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '../../lib/utils'
import type { WorkflowStep, StepType } from '../../types/workflow'

interface StepBlockProps {
  step: WorkflowStep
  isSelected: boolean
  onSelect: () => void
  onToggle: (enabled: boolean) => void
  onDelete: () => void
  onDuplicate: () => void
  onUpdate?: (updates: Partial<WorkflowStep>) => void
  className?: string
}

function getStepIcon(stepType: StepType) {
  switch (stepType) {
    case 'mouse_click':
    case 'mouse_double_click':
    case 'mouse_down':
    case 'mouse_up':
    case 'mouse_move':
    case 'drag_drop':
      return Mouse
    case 'scroll':
      return Scroll
    case 'type_text':
    case 'keyboard_shortcut':
      return Type
    case 'get_clipboard':
    case 'set_clipboard':
    case 'copy_selected_text':
    case 'paste_text':
      return Clipboard
    case 'navigate_url':
      return Navigation
    case 'take_screenshot':
      return Camera
    case 'task':
      return Bot
    case 'verify':
      return CheckCircle
    case 'notify':
      return Mail
    default:
      return Mouse
  }
}

function getStepDisplayName(stepType: StepType): string {
  switch (stepType) {
    case 'mouse_click':
      return 'Click'
    case 'mouse_double_click':
      return 'Double Click'
    case 'mouse_down':
      return 'Mouse Down'
    case 'mouse_up':
      return 'Mouse Up'
    case 'mouse_move':
      return 'Mouse Move'
    case 'drag_drop':
      return 'Drag & Drop'
    case 'scroll':
      return 'Scroll'
    case 'type_text':
      return 'Type Text'
    case 'keyboard_shortcut':
      return 'Keyboard Shortcut'
    case 'get_clipboard':
      return 'Get Clipboard'
    case 'set_clipboard':
      return 'Set Clipboard'
    case 'copy_selected_text':
      return 'Copy Selection'
    case 'paste_text':
      return 'Paste'
    case 'navigate_url':
      return 'Navigate to URL'
    case 'take_screenshot':
      return 'Take Screenshot'
    case 'task':
      return 'Task'
    case 'verify':
      return 'Verify'
    case 'notify':
      return 'Notify'
    default:
      return stepType
  }
}



function getCategoryColor(stepType: StepType): string {
  if (['mouse_click', 'mouse_double_click', 'mouse_down', 'mouse_up', 'mouse_move', 'drag_drop', 'scroll'].includes(stepType)) {
    return 'bg-blue-500'
  }
  if (['type_text', 'keyboard_shortcut', 'get_clipboard', 'set_clipboard', 'copy_selected_text', 'paste_text'].includes(stepType)) {
    return 'bg-green-500'
  }
  if (['navigate_url', 'take_screenshot'].includes(stepType)) {
    return 'bg-purple-500'
  }
  if (['task'].includes(stepType)) {
    return 'bg-orange-500'
  }
  if (['verify'].includes(stepType)) {
    return 'bg-emerald-500'
  }
  if (['notify'].includes(stepType)) {
    return 'bg-pink-500'
  }
  return 'bg-gray-500'
}

function renderStepSummary(stepType: StepType, params: any): React.ReactNode {
  switch (stepType) {
    case 'navigate_url':
      return (
        <div className="step-summary">
          {params.url ? (
            <span className="summary-text">{params.url}</span>
          ) : (
            <span className="config-prompt">Click to configure URL</span>
          )}
        </div>
      )
    case 'type_text':
      return (
        <div className="step-summary">
          {params.text && params.selector ? (
            <span className="summary-text">Type "{params.text}" in {params.selector}</span>
          ) : params.text ? (
            <span className="summary-text">Type "{params.text}"</span>
          ) : params.selector ? (
            <span className="summary-text">Type in {params.selector}</span>
          ) : (
            <span className="config-prompt">Click to configure text input</span>
          )}
        </div>
      )
    case 'mouse_click':
      return (
        <div className="step-summary">
          {params.selector ? (
            <span className="summary-text">Click {params.selector}</span>
          ) : (
            <span className="config-prompt">Click to configure selector</span>
          )}
        </div>
      )
    case 'take_screenshot':
      return (
        <div className="step-summary">
          {params.filename ? (
            <span className="summary-text">Screenshot: {params.filename}</span>
          ) : (
            <span className="summary-text">Take screenshot</span>
          )}
        </div>
      )
    case 'scroll':
      return (
        <div className="step-summary">
          <span className="summary-text">
            Scroll {params.direction || 'down'} {params.amount || 300}px
          </span>
        </div>
      )
    case 'task':
      return (
        <div className="step-summary">
          {params.description ? (
            <span className="summary-text">{params.description.slice(0, 60)}{params.description.length > 60 ? '...' : ''}</span>
          ) : (
            <span className="config-prompt">Click to configure AI task</span>
          )}
        </div>
      )
    case 'verify':
      return (
        <div className="step-summary">
          {params.description ? (
            <span className="summary-text">{params.description.slice(0, 60)}{params.description.length > 60 ? '...' : ''}</span>
          ) : (
            <span className="config-prompt">Click to configure verification</span>
          )}
        </div>
      )
    case 'notify':
      return (
        <div className="step-summary">
          <span className="summary-text">
            {params.message ? `Notify: ${params.message.slice(0, 40)}...` : 'Email notification on completion'}
          </span>
        </div>
      )
    case 'drag_drop':
      return (
        <div className="step-summary">
          {params.sourceSelector && params.targetSelector ? (
            <span className="summary-text">Drag {params.sourceSelector} → {params.targetSelector}</span>
          ) : (
            <span className="config-prompt">Click to configure drag & drop</span>
          )}
        </div>
      )
    default:
      return (
        <div className="step-summary">
          <span className="summary-text">Ready to execute</span>
        </div>
      )
  }
}

export function StepBlock({
  step,
  isSelected,
  onSelect,
  onToggle,
  onDelete,
  onDuplicate,
  className
}: StepBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: step.id,
    data: {
      type: 'step',
      step
    }
  })



  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const StepIcon = getStepIcon(step.step_type)
  const displayName = getStepDisplayName(step.step_type)
  const categoryColor = getCategoryColor(step.step_type)

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        "workflow-block transition-all duration-200 cursor-pointer hover:shadow-md",
        isSelected && "ring-2 ring-primary ring-offset-2",
        !step.enabled && "opacity-60",
        isDragging && "opacity-50 rotate-2 shadow-lg",
        className
      )}
      onClick={onSelect}
      data-dragging={isDragging}
    >
      <CardContent className="workflow-block-content">
        {/* Drag Handle Area - Full Left Side */}
        <div
          {...attributes}
          {...listeners}
          className="workflow-block-drag-handle"
          title="Drag to reorder"
        >
          <GripVertical className="drag-icon" />
        </div>

        {/* Main Content Area */}
        <div className="workflow-block-main">
          {/* Header Row */}
          <div className="workflow-block-header">
            <div className="workflow-block-title-row">
              <div className="workflow-block-indicators">
                <div className={cn("category-indicator", categoryColor)} />
                <StepIcon className="step-icon" />
              </div>
              <div className="workflow-block-title-content">
                <h4 className="step-title">{displayName}</h4>
                <div className="step-badges">
                  {!step.enabled && (
                    <Badge variant="secondary" className="step-badge">
                      Disabled
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Summary */}
          <div className="workflow-block-summary">
            {renderStepSummary(step.step_type, step.params)}
          </div>

          {/* Controls Footer */}
          <div className="workflow-block-controls">
            <div className="controls-left">
              <div className="enable-control">
                <span className="control-label">Enabled</span>
                <Switch
                  checked={step.enabled}
                  onCheckedChange={onToggle}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            <div className="controls-right">
              <Button
                variant="ghost"
                size="sm"
                className="control-btn delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                title="Delete step"
              >
                <X className="h-3 w-3" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="control-btn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSelect(); }}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); onToggle(!step.enabled); }}
                  >
                    {step.enabled ? (
                      <>
                        <EyeOff className="h-4 w-4 mr-2" />
                        Disable
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        Enable
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
